import { Trans, useLingui } from '@lingui/react';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { Table } from '@startuptools/ui/Table';
import { ButtonGroup } from '@startuptools/ui/ButtonGroup';
import { IconButton } from '@startuptools/ui/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { Typography } from '@startuptools/ui/Typography';
import { Button } from '@startuptools/ui/Button';
import { Stack } from '@startuptools/ui/Stack';
import { Alert } from '@startuptools/ui/Alert';
import { GenericError } from '@startuptools/ui/Error';
import { type GqlCapTableStake, GqlCurrency } from '../../../graphql/react-operations';
import { type InvestmentRound } from './types';
import React from 'react';
import { type DisplayIdentity, Identity } from '@startuptools/business-ui/identity/Identity';
import { JuridicalKind } from '../../../models/identity.model';
import { InputState } from '@startuptools/business-ui/identity/types';
import { AddIdentity } from '@startuptools/business-ui/identity/add/AddIdentity';
import { BigNumber } from 'bignumber.js';
import { IDKind } from '@startuptools/common/common';
import { identityToInput } from '@startuptools/business-ui/identity/identity-to-input';

const UNASSIGNED_SHARES_IDENTITY = {
  name: 'Unassigned',
  identityNumber: '19000101-1234',
  juridicalKind: JuridicalKind.Company,
  identityNumberKind: IDKind.Fictitious,
  isSwedish: true,
} as const;

const InvestorRow = ({
  identity,
  editable,
  quantity,
  amount,
  setQuantity,
  deleteRow,
}: {
  identity: DisplayIdentity;
  editable: boolean;
  quantity: string;
  setQuantity?: (quantity: string) => void;
  amount: string;
  deleteRow?: () => void;
}) => {
  const { i18n } = useLingui();

  return (
    <tr>
      <td>
        <Identity
          editable={false}
          identity={identity}
          renderName={name => (name === 'Unassigned' ? <Trans id="Unassigned" /> : name)}
        />
      </td>
      <td>
        <NumericFormatInput
          name="quantity"
          onChange={e => {
            setQuantity?.(e.target.value);
          }}
          readOnly={!editable}
          lang={i18n.locale}
          value={quantity}
        />
      </td>

      <td>
        <NumericFormatInput name="amount" readOnly lang={i18n.locale} value={amount} endDecorator={GqlCurrency.Sek} />
      </td>

      <td>
        {editable && (
          <ButtonGroup onClick={deleteRow}>
            <IconButton color="danger">
              <DeleteIcon />
            </IconButton>
          </ButtonGroup>
        )}
      </td>
    </tr>
  );
};

type Row = Pick<GqlCapTableStake, 'stake'> & {
  identity: Omit<DisplayIdentity, 'id' | 'isSwedish'> & { id?: string | null; juridicalKind: JuridicalKind };
};
export const AssignInvestors = ({
  round,
  loading,
  error,
  onSave,
}: {
  round: InvestmentRound;
  onSave: (rows: Row[]) => Promise<void>;
  loading: boolean;
  error: boolean;
}) => {
  const [rows, setRows] = React.useState<Row[]>([]);
  const quantityLeft = rows.reduce((acc, row) => acc - Number(row.stake), Number(round.count));
  const noneAssigned = quantityLeft === Number(round.count);
  const amountLeft = new BigNumber(quantityLeft).times(round.pricePerShare);
  const [overAssigned, setOverAssigned] = React.useState(false);
  const [showIdentityDialog, setShowIdentityDialog] = React.useState(false);
  return (
    <>
      {showIdentityDialog && (
        <AddIdentity
          headline={<Trans id="Add owner" />}
          juridicalKinds={[JuridicalKind.Person, JuridicalKind.Company]}
          availableInputStates={{
            [InputState.System]: true,
            [InputState.CompanyBoard]: false,
            [InputState.Swedish]: true,
            [InputState.Foreign]: true,
          }}
          defaultInputState={InputState.Swedish}
          detailFieldsSettings={{}}
          handleCreate={async function (identity) {
            setRows(rows => [...rows, { stake: '', identityId: identity.id, identity: identityToInput(identity) }]);
            setShowIdentityDialog(false);
          }}
          onClose={() => {
            setShowIdentityDialog(false);
          }}
        />
      )}
      <Stack gap={2}>
        <Typography level="body-sm">
          <Trans id="Add investors to this round below. If you don't know yet who the investor will be, you can leave them unassigned. Unassigned shares are gathered as a separate stake in the CapTable." />
        </Typography>
        {overAssigned && (
          <Alert color="warning">
            <Trans
              id="The round is overassigned by {count} shares. Reduce the shares of 1 or more investors."
              values={{ count: -quantityLeft }}
            />
          </Alert>
        )}
        {error && <GenericError />}
        <Stack direction="row" gap={2}>
          <Button onClick={() => setShowIdentityDialog(true)} disabled={amountLeft.isEqualTo(0)}>
            <Trans id="Add investor" />
          </Button>

          <Button
            variant={noneAssigned ? 'outlined' : undefined}
            loading={loading}
            onClick={async () => {
              if (quantityLeft < 0) {
                return setOverAssigned(true);
              }
              const toSave = [...rows];
              if (quantityLeft > 0) {
                toSave.push({
                  identity: UNASSIGNED_SHARES_IDENTITY,
                  stake: quantityLeft.toString(),
                });
              }

              await onSave(toSave);
            }}
          >
            {noneAssigned ? <Trans id="Continue without investors" /> : <Trans id="Continue" />}
          </Button>
        </Stack>

        <Table sx={{ '& tr th:last-child, & tr td:last-child': { width: '60px' } }}>
          <thead>
            <tr>
              <th>
                <Trans id="Investor" />
              </th>
              <th>
                <Trans id="Quantity" />
              </th>
              <th>
                <Trans id="Invested Amount" />
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <InvestorRow
                key={r.identity.id}
                editable
                identity={r.identity as DisplayIdentity}
                quantity={r.stake}
                setQuantity={quantity => {
                  setRows(rows =>
                    rows.map(row => {
                      if (row.identity.id === r.identity.id) {
                        return {
                          ...row,
                          stake: quantity,
                        };
                      }
                      return row;
                    }),
                  );
                }}
                amount={new BigNumber(r.stake).times(round.pricePerShare).toString()}
                deleteRow={() => {
                  setRows(rows => rows.filter(row => row.identity.id !== r.identity.id));
                }}
              />
            ))}
            <InvestorRow
              editable={false}
              quantity={quantityLeft > 0 ? quantityLeft.toString() : '0'}
              amount={amountLeft.gt(0) ? amountLeft.toString() : '0'}
              identity={{ ...UNASSIGNED_SHARES_IDENTITY, id: 'unassigned-temporary-id' }}
            />
          </tbody>
        </Table>
      </Stack>
    </>
  );
};
